import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { ReferenceNameControllerService } from "../../../services/reference-name-controller.service";
import { UserRightsService } from "../../../services/user-rights.service";
import { NgClass } from "@angular/common";
import { ButtonComponent } from "../../elements/button/button.component";
import { UserManagerComponent } from "./user-manager/user-manager.component";
import { ProfileManagerComponent } from "./profile-manager/profile-manager.component";
import { ReferenceManagerComponent } from "./reference-manager/reference-manager.component";
import { DeviceOffContractManagerComponent } from "./device-off-contract-manager/device-off-contract-manager.component";
import { SwitchComponent } from "./switch/switch.component";
import { TranslateModule } from "@ngx-translate/core";

interface NavObject {
  navName: string;
  isOpen: boolean;
  selectorStyle: string;
  navLogo?: string;
  style?: string;
}
@Component({
  selector: "app-manager",
  templateUrl: "./manager.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./manager.component.scss"],
  imports: [
    NgClass,
    ButtonComponent,
    UserManagerComponent,
    ProfileManagerComponent,
    ReferenceManagerComponent,
    DeviceOffContractManagerComponent,
    SwitchComponent,
    TranslateModule
  ]
})
export class ManagerComponent implements OnInit, OnChanges, OnDestroy {
  //@Input() loading: string;
  @Input() referenceLoading: boolean | undefined;

  private readonly SELECTOR_STYLE_OPEN = "nav-selector-open";
  private readonly SELECTOR_STYLE_CLOSE = "nav-selector-close";
  private readonly NAVIGATOR_STYLE_ENABLED = "navigator";
  private readonly NAVIGATOR_STYLE_DISABLED = "navigator-disabled";

  /**
   * URL first segment under /manager (/manager/<slug>/...) ↔ internal
   * selectedNav. "references" carries a required sub-segment, so navigating to
   * the Reference section targets its default sub-route (references/sites).
   */
  private readonly SLUG_TO_NAV: { [slug: string]: string } = {
    users: "Users",
    groups: "Groups",
    references: "Reference",
    trackers: "Devices",
    tokens: "Token"
  };
  private readonly NAV_TO_PATH: { [nav: string]: string[] } = {
    Users: ["/manager", "users"],
    Groups: ["/manager", "groups"],
    Reference: ["/manager", "references", "sites"],
    Devices: ["/manager", "trackers"],
    Token: ["/manager", "tokens"]
  };

  private subscription: Subscription = new Subscription();

  selectedIndex: number;

  navObject: NavObject[] = [
    {
      navName: "Users",
      isOpen: true,
      selectorStyle: this.SELECTOR_STYLE_OPEN,
      navLogo: "user"
    },
    {
      navName: "Groups",
      isOpen: false,
      selectorStyle: this.SELECTOR_STYLE_CLOSE,
      navLogo: "group"
    },
    {
      navName: "Reference",
      isOpen: false,
      selectorStyle: this.SELECTOR_STYLE_CLOSE,
      navLogo: "reference"
    },
    {
      navName: "Devices",
      isOpen: false,
      selectorStyle: this.SELECTOR_STYLE_CLOSE,
      navLogo: "device"
    }
  ];

  tokenNavObject: NavObject = {
    navName: "Token",
    isOpen: false,
    selectorStyle: this.SELECTOR_STYLE_CLOSE,
    navLogo: "token"
  };

  selectedNav: string = "Users";

  navStyle: string = this.NAVIGATOR_STYLE_ENABLED;

  constructor(
    public referenceNameControllerService: ReferenceNameControllerService,
    private userRightsService: UserRightsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    // Route-driven: the first path segment under /manager selects the section;
    // reacts to deep-links and browser back/forward. The router guarantees a
    // section slug is present (/manager redirects to /manager/users), so the
    // "Users" fallback below is only a safety net. Sibling section routes
    // recreate this component, so reading the snapshot on init is sufficient.
    this.subscription.add(
      this.route.url.subscribe((segments) => {
        const slug = segments.length ? segments[0].path : "";
        this.applyNav(this.SLUG_TO_NAV[slug] ?? "Users");
      })
    );
  }

  ngOnChanges() {
    this.navStyle = this.referenceLoading
      ? this.NAVIGATOR_STYLE_DISABLED
      : this.NAVIGATOR_STYLE_ENABLED;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onNavChange(event: NavObject) {
    if (this.referenceLoading === undefined) {
      // Navigation drives selection; the route subscription applies it.
      this.router.navigate(this.NAV_TO_PATH[event.navName]);
    }
  }

  private applyNav(navName: string): void {
    for (const nav of this.navObject) {
      nav.isOpen = false;
      nav.selectorStyle = this.SELECTOR_STYLE_CLOSE;
    }
    this.tokenNavObject.isOpen = false;
    this.tokenNavObject.selectorStyle = this.SELECTOR_STYLE_CLOSE;

    const target =
      this.navObject.find((nav) => nav.navName === navName) ??
      (navName === this.tokenNavObject.navName
        ? this.tokenNavObject
        : undefined);
    if (target) {
      target.isOpen = true;
      target.selectorStyle = this.SELECTOR_STYLE_OPEN;
    }
    this.selectedNav = navName;
  }

  isIBMAdmin(): boolean {
    return this.userRightsService.getUserRightLevel("IBMAdmin");
  }
}
